import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import ToastNotification from "common/toast/Toast";
import {
  formatCurrency,
  FREE_SHIPPING_THRESHOLD,
  getCurrentPrice,
  getPromoHint,
} from "config/config";
import useCartDetails from "hooks/useCartDetails";

import { FlipAmount, FlipText, LoadingDots, ProductImage } from "@/common";
// The common barrel re-exports Toast's named methods but not the `ToastNotification`
// object itself — import the default directly, as CartPage does.
import { Button, CartLineItem, Icon } from "@/design-system";
import {
  clearCart,
  closeCartDrawer,
  removeFromCart,
  updateQuantity,
} from "@/redux/slices/cartSlice";

/**
 * The slide-in cart drawer — the reference's CART DRAWER surface (grep `CART DRAWER`
 * in `Dens N Dente Tailwind.dc.html`), opened from the header's cart button via
 * `cartSlice.drawerOpen`. Mounted once in `App.js`, ALWAYS mounted: `.cart-morph`
 * (keyframes.js) slides it in off `data-open`, so conditional rendering would skip
 * the animation. This is the Aug 2026 plain-slide version, not the old morph.
 *
 * Anatomy, top to bottom, all 1:1 with the reference unless noted:
 * - Header: "Your cart" + live item count, Empty cart (when items), close ×, and the
 *   free-delivery progress bar. The header's bottom border fades in once the list is
 *   scrolled (`onScroll` toggles it, exactly upstream's `cartScrolled`).
 * - `.cart-list`: empty state, or one `CartLineItem cl-compact` per line. Lines are
 *   priced through `getCurrentPrice` (tier/promo aware): the struck original and the
 *   green "You save" pill appear only when it reports a real discount, and a line
 *   whose item is on auto-delivery gets the blue hint pill instead — promo wins when
 *   both apply, matching the component's either/or contract. A Buy-X-Get-Y item below
 *   its promotion's minimum shows "Add N to cart to get promotion price" in the hint
 *   pill (`getPromoHint`), ahead of "On auto-delivery".
 * - Auto-delivery band → `/re-order`. The reference opens an in-drawer scheduling
 *   flow here (`openCartSchedule`); this app already has a page for that, so the
 *   band navigates — the in-drawer flow is a backlog item.
 * - Footer: Subtotal with the digit-flip animation, a savings row (only when > 0 —
 *   upstream renders "−$0.00" unconditionally, which reads broken), the tax/delivery
 *   disclaimer, Checkout, "View full cart" → `/cart`, Continue shopping.
 *
 * The progress bar tracks the app's REAL threshold — `FREE_SHIPPING_THRESHOLD`
 * ($300, `config/config.js`, the same number `calculateOrderTotal` uses) — not the
 * reference's demo $250. A bar that congratulates the buyer $50 before checkout
 * agrees would be worse than no bar.
 *
 * Subtotal here is Σ line totals AFTER discounts (what `getCurrentPrice` returns),
 * so the savings row is information, not arithmetic — upstream's gross-minus-savings
 * presentation doesn't survive contact with tier pricing, where there is no
 * meaningful gross.
 */

export default function CartDrawer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const open = useSelector((state) => state.cart.drawerOpen);
  const cartItems = useSelector((state) => state.cart.items);
  const { cartDetails } = useCartDetails();

  const [scrolled, setScrolled] = useState(false);
  const closeButtonRef = useRef(null);

  const close = () => dispatch(closeCartDrawer());

  // The reference's openCart/closeCart lock body scroll; doing it as an effect also
  // restores scroll if the drawer unmounts while open.
  useEffect(() => {
    if (!open) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") dispatch(closeCartDrawer());
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, dispatch]);

  const itemCount = cartItems.reduce((total, item) => {
    const quantity = Number(item.quantity);
    return total + (Number.isFinite(quantity) && quantity > 0 ? quantity : 1);
  }, 0);
  const hasItems = cartItems.length > 0;

  // Cart-owned fields (quantity, subscription) read LIVE from the slice, product
  // fields from the fetched details. `useCartDetails` refetches on every cart change
  // and NetSuite can take seconds, so a line that trusted the merged snapshot's
  // quantity would snap back to the old value between the stepper click and the
  // refetch landing.
  const liveById = useMemo(
    () => new Map(cartItems.map((item) => [item.id, item])),
    [cartItems]
  );

  const lines = useMemo(
    () =>
      cartDetails
        .filter((item) => liveById.has(item.id))
        .map((item) => {
          const live = liveById.get(item.id);
          const quantity = live.quantity;
          const { basePrice, currentPrice, savePrice } = getCurrentPrice(
            item,
            quantity
          );
          const lineTotal = currentPrice * quantity;
          const lineSave = savePrice * quantity;
          return {
            item,
            quantity,
            price: currentPrice,
            total: lineTotal,
            original: savePrice > 0 ? basePrice * quantity : undefined,
            save: lineSave,
            promo:
              savePrice > 0
                ? `You save ${formatCurrency(lineSave)}`
                : undefined,
            hint:
              getPromoHint(item, quantity) ||
              (live.custrecord_ci_sub_enabled ? "On auto-delivery" : undefined),
          };
        }),
    [cartDetails, liveById]
  );

  const subtotal = lines.reduce((sum, line) => sum + line.total, 0);
  const savings = lines.reduce((sum, line) => sum + line.save, 0);

  // Until the first details fetch lands there are no prices at all — a real
  // "$0.00" subtotal and "$300.00 to free delivery" would both be confidently
  // wrong, so the money areas show neutral placeholders instead.
  const pricesPending = hasItems && lines.length === 0;

  const remain = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);
  const freeShip = hasItems && !pricesPending && remain <= 0;
  const shipPct = pricesPending
    ? 0
    : Math.min(100, Math.round((subtotal / FREE_SHIPPING_THRESHOLD) * 100));
  const shipHint = pricesPending
    ? "Updating…"
    : freeShip
      ? "Free delivery unlocked"
      : `${formatCurrency(remain)} to free delivery`;

  const handleQuantityChange = (itemId, newQuantity) => {
    if (newQuantity === 0) dispatch(removeFromCart(itemId));
    else dispatch(updateQuantity({ id: itemId, quantity: newQuantity }));
  };

  const handleEmptyCart = () => {
    dispatch(clearCart());
    ToastNotification.success("Cart emptied", { sub: "All items removed" });
  };

  const goTo = (path) => {
    close();
    navigate(path);
  };

  return (
    <>
      {/* Scrim — the reference's `scrimStyle`, its own fade so the drawer's slide
          doesn't drag the backdrop with it. */}
      <div
        onClick={close}
        aria-hidden="true"
        className="fixed inset-0 z-[500] bg-[var(--surface-overlay)] backdrop-blur-[3px] [transition:opacity_400ms_cubic-bezier(0.22,1,0.36,1)]"
        style={{
          opacity: open ? 1 : 0,
          pointerEvents: open ? "auto" : "none",
        }}
      />
      <aside
        className="cart-morph"
        data-open={open ? "true" : "false"}
        role="dialog"
        aria-modal="true"
        aria-label="Cart"
        aria-hidden={!open}
      >
        <div className="cart-morph-inner">
          <div
            className={`shrink-0 border-b ${
              scrolled ? "border-line-subtle" : "border-transparent"
            }`}
          >
            <div className="flex items-center gap-[14px] px-[26px] pb-[13px] pt-[18px]">
              <div className="min-w-0 flex-1">
                <div className="mb-[5px] text-2xs font-bold uppercase tracking-[var(--tracking-wider)] text-ink-3">
                  Your cart
                </div>
                <div className="text-xl font-semibold leading-none">
                  <FlipText
                    text={`${itemCount} ${itemCount === 1 ? "item" : "items"}`}
                  />
                </div>
              </div>
              {hasItems && (
                <button
                  onClick={handleEmptyCart}
                  className="inline-flex h-[38px] shrink-0 cursor-pointer items-center gap-[6px] rounded-pill border-0 bg-transparent px-[10px] font-sans text-xs font-semibold text-ink-3 [transition:color_var(--duration-fast),background_var(--duration-fast)] hover:bg-error-50 hover:text-error-600"
                >
                  <Icon name="remove_shopping_cart" size={18} />
                  Empty cart
                </button>
              )}
              <button
                ref={closeButtonRef}
                onClick={close}
                aria-label="Close"
                className="inline-flex h-[38px] w-[38px] shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-transparent text-ink-2 [transition:background_var(--duration-fast),color_var(--duration-fast)] hover:bg-neutral-100 hover:text-ink active:scale-[0.92]"
              >
                <Icon name="close" size={22} />
              </button>
            </div>
            <div className="flex items-center gap-[11px] px-[26px] pb-[15px]">
              <div
                className="h-[6px] min-w-0 flex-1 overflow-hidden rounded-pill bg-neutral-100"
                role="presentation"
              >
                <div
                  className="h-full rounded-pill [transition:width_var(--duration-slow)_var(--ease-out)]"
                  style={{
                    width: `${shipPct}%`,
                    background: freeShip
                      ? "var(--success-500)"
                      : "var(--blue-500)",
                  }}
                />
              </div>
              <span
                className="shrink-0 text-[12px] font-semibold"
                style={{
                  color: freeShip ? "var(--success-700)" : "var(--blue-800)",
                }}
              >
                <FlipText swap text={shipHint} />
              </span>
            </div>
          </div>

          <div
            className="cart-list flex-1 overflow-y-auto px-[26px] py-0"
            onScroll={(event) => setScrolled(event.target.scrollTop > 2)}
          >
            {!hasItems ? (
              <div className="px-[var(--space-4)] py-[var(--space-12)] text-center">
                <span className="mb-[14px] inline-flex h-[60px] w-[60px] items-center justify-center rounded-full bg-soft text-blue-600">
                  <Icon name="shopping_cart" size={30} />
                </span>
                <div className="mb-[var(--space-1)] text-lg font-semibold">
                  Your cart is empty
                </div>
                <p className="mx-0 mb-[var(--space-4)] mt-0 text-sm text-ink-2">
                  Browse the catalogue to add your essentials.
                </p>
                <Button
                  startIcon="storefront"
                  onClick={() => goTo("/products")}
                >
                  Shop the catalogue
                </Button>
              </div>
            ) : pricesPending ? (
              <LoadingDots className="py-10" />
            ) : (
              lines.map(
                ({ item, price, total, original, promo, hint }, idx) => (
                  <div key={`${item.id}-${idx}`}>
                    {/* name = itemid, sku = mpn — the same fields the listing card shows
                      (ProductInListGrid), so a product keeps one name from card to cart. */}
                    <CartLineItem
                      className="cl-compact"
                      name={item.itemid || "Unavailable item"}
                      sku={item.mpn || undefined}
                      media={
                        <ProductImage
                          src={item.file_url}
                          alt={item.itemid}
                          className="h-full w-full object-cover"
                        />
                      }
                      price={price}
                      quantity={item.quantity}
                      total={total}
                      original={original}
                      promo={promo}
                      hint={promo ? undefined : hint}
                      math
                      onQuantityChange={(quantity) =>
                        handleQuantityChange(item.id, quantity)
                      }
                      onRemove={() => dispatch(removeFromCart(item.id))}
                    />
                  </div>
                )
              )
            )}
          </div>

          {hasItems && (
            <button
              onClick={() => goTo("/re-order")}
              className="group flex w-full shrink-0 cursor-pointer items-center gap-[10px] border-0 border-t border-line-subtle bg-card px-[26px] py-[11px] text-left font-sans shadow-[0_-10px_14px_-12px_rgba(16,38,59,.38)] [transition:background_var(--duration-fast)] hover:bg-neutral-50"
            >
              <Icon
                name="autorenew"
                size={19}
                className="shrink-0 text-blue-600"
              />
              <span className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
                Set your staples on auto-delivery
              </span>
              <Icon
                name="chevron_right"
                size={18}
                className="shrink-0 text-ink-3 [transition:transform_var(--duration-base)_var(--ease-spring),color_var(--duration-fast)] group-hover:translate-x-[3px] group-hover:text-blue-700"
              />
            </button>
          )}

          <div className="flex flex-col gap-[13px] border-t border-line-subtle bg-neutral-50 px-[26px] pb-[20px] pt-[18px]">
            <div className="flex flex-col gap-[8px]">
              <div className="flex items-baseline justify-between pb-[2px]">
                <span className="text-sm font-semibold text-ink">Subtotal</span>
                {pricesPending ? (
                  <LoadingDots />
                ) : (
                  <FlipAmount
                    text={formatCurrency(subtotal)}
                    className="font-bold text-[24px] leading-none tracking-[var(--tracking-tight)]"
                  />
                )}
              </div>
              {savings > 0 && (
                <div className="flex justify-between text-[13px]">
                  <span className="text-ink-2">Promo savings</span>
                  <b className="font-semibold text-success-700">
                    −{formatCurrency(savings)}
                  </b>
                </div>
              )}
              <div className="flex justify-between text-[13px]">
                <span className="text-ink-2">Delivery &amp; HST</span>
                <span className="text-ink-2">Calculated at checkout</span>
              </div>
            </div>
            <Button
              fullWidth
              size="lg"
              endIcon="chevron_right"
              disabled={!hasItems || pricesPending}
              onClick={() => goTo("/checkout")}
            >
              Checkout
            </Button>
            <div className="flex items-center justify-center gap-[10px] text-xs">
              {hasItems && (
                <>
                  <button
                    onClick={() => goTo("/cart")}
                    className="v2link cursor-pointer border-0 bg-transparent p-0 font-sans text-[13px] font-semibold text-blue-700 hover:text-blue-800"
                  >
                    View full cart
                    <Icon name="chevron_right" size={14} className="chev" />
                  </button>
                  <span
                    className="h-[12px] w-px bg-[var(--border-subtle)]"
                    aria-hidden="true"
                  />
                </>
              )}
              <button
                onClick={close}
                className="cursor-pointer border-0 bg-transparent p-0 font-sans text-[13px] font-semibold text-ink-2 hover:text-ink"
              >
                Continue shopping
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
